import {
  CALLOUT_COLOR, CALLOUT_FILL_OPACITY, polygonLabelPoint, type Callout,
} from '@/lib/callouts'
import { WORLD_GROUP_STYLE, pinAt, visibleFrom } from '@/lib/liveViewport'

/**
 * Mevkileri haritaya basan katman. Stratejik nokta editörü ve strateji
 * editörü aynı katmanı kullanır, böylece bir mevki iki ekranda aynı görünür.
 *
 * Katman **tıklanmaz** (`pointer-events: none`). Mevkiler geniş alanlar;
 * tıklanabilir olsalardı üstlerinden haritayı sürüklemek mümkün olmazdı.
 * Seçim, tıklamanın dünya koordinatından `calloutAt()` ile yapılır.
 *
 * Çizgi iki kez basılır (altta siyah halo, üstünde turuncu), kontrol
 * bölgesinin kuralı: tek kat ince çizgi parlak tarlada kayboluyor. Kalınlık
 * ekran pikselindedir, zoom'dan bağımsız.
 */

interface Vpt { a: number; e: number; f: number }

interface Props {
  callouts: Callout[]
  vpt: Vpt
  selectedId?: number | null
  zIndex?: number
  /**
   * Konum CSS değişkenlerinden (strateji editörünün canlı viewport'u, bkz.
   * `lib/liveViewport.ts`): çokgenler dünya koordinatında, adlar iğne. Zoom
   * sırasında katman hiç render olmaz. `vpt` o zaman kullanılmaz.
   */
  live?: boolean
}

/** Ekrandaki kutusu bundan küçük mevkinin adı basılmaz; ad bölgeyi örterdi. */
const LABEL_MIN_PX = 36

const TEXT_HALO = '0 1px 2px #000, 1px 0 2px #000, -1px 0 2px #000, 0 -1px 2px #000'

export default function CalloutLayer({ callouts, vpt, selectedId = null, zIndex = 2, live }: Props) {
  if (callouts.length === 0) return null
  if (live) return <LiveCallouts callouts={callouts} selectedId={selectedId} zIndex={zIndex} />
  const sx = (x: number) => x * vpt.a + vpt.e
  const sy = (y: number) => y * vpt.a + vpt.f

  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex }}>
      <svg className="absolute inset-0 h-full w-full" style={{ overflow: 'visible' }} aria-hidden>
        {callouts.map(c => {
          if (c.points.length < 3) return null
          const pts = c.points.map(p => `${sx(p.x)},${sy(p.y)}`).join(' ')
          const selected = c.id === selectedId
          return (
            <g key={c.id}>
              <polygon points={pts} fill={CALLOUT_COLOR}
                fillOpacity={selected ? CALLOUT_FILL_OPACITY * 1.8 : CALLOUT_FILL_OPACITY}
                stroke="#000" strokeOpacity={0.55} strokeWidth={selected ? 6 : 4.5} strokeLinejoin="round" />
              <polygon points={pts} fill="none"
                stroke={CALLOUT_COLOR} strokeWidth={selected ? 2.5 : 1.75} strokeLinejoin="round" />
            </g>
          )
        })}
      </svg>

      {callouts.map(c => {
        if (c.points.length < 3 || !c.name) return null
        const xs = c.points.map(p => sx(p.x))
        const ys = c.points.map(p => sy(p.y))
        const w = Math.max(...xs) - Math.min(...xs)
        const h = Math.max(...ys) - Math.min(...ys)
        if (Math.max(w, h) < LABEL_MIN_PX && c.id !== selectedId) return null
        const at = polygonLabelPoint(c.points)
        return (
          // Mevki adı **veridir**, büyütülmez.
          <span
            key={c.id}
            className="absolute whitespace-nowrap text-[11px] font-semibold"
            style={{
              left: sx(at.x), top: sy(at.y),
              transform: 'translate(-50%, -50%)',
              color: CALLOUT_COLOR,
              textShadow: TEXT_HALO,
            }}
          >
            {c.name}
          </span>
        )
      })}
    </div>
  )
}

/** Canlı viewport'lu hâl: bkz. `live`. */
function LiveCallouts({ callouts, selectedId, zIndex }: { callouts: Callout[]; selectedId: number | null; zIndex: number }) {
  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex }}>
      <svg className="absolute inset-0 h-full w-full" style={{ overflow: 'visible' }} aria-hidden>
        <g style={WORLD_GROUP_STYLE}>
          {callouts.map(c => {
            if (c.points.length < 3) return null
            const pts = c.points.map(p => `${p.x},${p.y}`).join(' ')
            const selected = c.id === selectedId
            return (
              <g key={c.id}>
                <polygon points={pts} fill={CALLOUT_COLOR}
                  fillOpacity={selected ? CALLOUT_FILL_OPACITY * 1.8 : CALLOUT_FILL_OPACITY}
                  stroke="#000" strokeOpacity={0.55} strokeWidth={selected ? 6 : 4.5} strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke" />
                <polygon points={pts} fill="none"
                  stroke={CALLOUT_COLOR} strokeWidth={selected ? 2.5 : 1.75} strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke" />
              </g>
            )
          })}
        </g>
      </svg>

      {callouts.map(c => {
        if (c.points.length < 3 || !c.name) return null
        const xs = c.points.map(p => p.x), ys = c.points.map(p => p.y)
        const extent = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys))
        const at = polygonLabelPoint(c.points)
        return (
          <span
            key={c.id}
            className="absolute whitespace-nowrap text-[11px] font-semibold"
            style={{
              left: 0, top: 0,
              transform: `${pinAt(at.x, at.y)} translate(-50%, -50%)`,
              // Kutusu LABEL_MIN_PX'ten küçükken ad basılmaz — eşik CSS'te.
              opacity: c.id === selectedId ? 1 : visibleFrom(extent > 0 ? LABEL_MIN_PX / extent : Infinity),
              color: CALLOUT_COLOR,
              textShadow: TEXT_HALO,
            }}
          >
            {c.name}
          </span>
        )
      })}
    </div>
  )
}
